import {
  GetCoordinatesParams,
  GetReportDifferenceParams,
  PullPeopleReportParams,
  SearchPeopleReportParams,
  PullPropertyReportParams,
  SocialSearch,
  PullPhoneReportParams,
  GetRentDataParams,
  SubmitComplaintParams,
  PropertyReportDifferenceParams,
  GetPropertyPOIsParams,
  preForeclosureSearchParams,
  recentSaleSearchParams,
} from "./reportTypes/";

import { store } from "../../store/index";
import { logoutWithRedirect } from "../authentication/authenticationSlice";
import { rateLimitAsync } from "../admin/adminSlice";
import {
  PullVehicleMarketEstimateParams,
  PullVehicleReportParams,
} from "./reportTypes/vehicleReportTypes";
import {
  PullNeighborhoodReportParams,
  PullNeighborhoodSearchParams,
} from "./reportTypes/neighborhoodReportTypes";
import {
  CombinedAddReportToList,
  DeleteListParams,
  GetSpecificListParams,
  RemoveReportFromListParams,
  RenameListParams,
} from "./reportTypes/listTypes";
import {
  CombinedNoteParams,
  RemoveReportNoteParams,
  UpdateReportNoteParams,
} from "./reportTypes/noteTypes";
import {
  setAccelerateBilling,
  setShowAccelerateBanner,
  setShowSecondaryAccelerate,
} from "../subscriptions/subscriptionSlice";

export function getReportHistory() {
  return new Promise((resolve, reject) => {
    fetch("/api/getReportHistory", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function pullPeopleReport({ reportToken }: PullPeopleReportParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/pullPeopleReport", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ reportToken }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "pullPeopleReport.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store
            .dispatch(rateLimitAsync({ action: "pullPeopleReport" }))
            .then(() => {
              store.dispatch(
                logoutWithRedirect({
                  location: "pullPeopleReport.rateLimit",
                }),
              );
              reject({ message: "Rate Limit, authorization denied" });
            });
        } else {
          return response.json();
        }
      })
      .then(data => {
        const accountType = store.getState().authentication.accountType;
        const variant =
          store.getState().authentication?.memberExperience?.variant;
        if (data.success) {
          if (accountType !== "people" && variant === "m48") {
            store.dispatch(setShowAccelerateBanner(true));
          }
          resolve(data);
        } else {
          if (data.message === "Plan Exceeded") {
            if (store.getState().subscription.isInTrial) {
              if (accountType !== "people" && variant === "m48") {
                store.dispatch(setShowSecondaryAccelerate(true));
              } else {
                store.dispatch(setAccelerateBilling());
              }
            } else {
              alert(
                "Your membership has exceeded normal usage for the given billing period.  Please contact member support to resolve this issue.",
              );
            }
            console.log("People:", data.message);
          }
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function getReportDifference({
  reportToken,
  versionA,
  versionB,
}: GetReportDifferenceParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/getReportDifference", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ reportToken, versionA, versionB }),
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function searchPeople({
  firstName,
  lastName,
  minAge,
  maxAge,
  city,
  state,
}: SearchPeopleReportParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/searchPeople", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        firstName,
        lastName,
        minAge,
        maxAge,
        city,
        state,
      }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "searchPeople.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store
            .dispatch(rateLimitAsync({ action: "searchPeople" }))
            .then(() => {
              store.dispatch(
                logoutWithRedirect({
                  location: "searchPeople.rateLimit",
                }),
              );
              reject({ message: "Rate Limit, authorization denied" });
            });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function getCoordinates(addressArray: GetCoordinatesParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/getCoordinates", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ addresses: addressArray }),
    })
      .then(response => response.json())
      .then(data => resolve(data))
      .catch(error => reject(error));
  });
}

export function pullPropertyReport({
  addressLine1,
  addressLine2,
  state,
  city,
  zip,
  latitude,
  longitude,
  nearbySchoolRadius,
  propertyId,
  searchMethod,
  addressComponents: {
    streetNumber,
    streetName,
    subPremise,
    zip4,
    county,
    neighborhood,
    sublocality,
    premise,
    floor,
    establishment,
  },
}: PullPropertyReportParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/pullPropertyReport", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        addressLine1,
        addressLine2,
        state,
        city,
        zip,
        latitude,
        longitude,
        nearbySchoolRadius,
        propertyId,
        searchMethod,
        addressComponents: {
          streetNumber,
          streetName,
          subPremise,
          zip4,
          county,
          neighborhood,
          sublocality,
          premise,
          floor,
          establishment,
        },
      }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "pullPropertyReport.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store
            .dispatch(rateLimitAsync({ action: "pullPropertyReport" }))
            .then(() => {
              store.dispatch(
                logoutWithRedirect({
                  location: "pullPropertyReport.rateLimit",
                }),
              );
              reject({ message: "Rate Limit, authorization denied" });
            });
        } else {
          return response.json();
        }
      })
      .then(data => {
        const accountType = store.getState().authentication.accountType;
        const variant =
          store.getState().authentication?.memberExperience?.variant;
        if (data.success) {
          if (accountType !== "property" && variant === "m48") {
            store.dispatch(setShowAccelerateBanner(true));
          }
          resolve(data);
        } else {
          if (data.message === "Plan Exceeded") {
            if (store.getState().subscription.isInTrial) {
              if (accountType !== "property" && variant === "m48") {
                store.dispatch(setShowSecondaryAccelerate(true));
              } else {
                store.dispatch(setAccelerateBilling());
              }
            } else {
              alert(
                "Your membership has exceeded normal usage for the given billing period.  Please contact member support to resolve this issue.",
              );
            }
            console.log("Property:", data.message);
          }
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function preForeclosureSearch({
  bbox,
  pageNumber,
  pageSize,
  preForeclosureSearchType,
  searchPhrase,
  latitude,
  longitude,
  radius,
}: preForeclosureSearchParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/preForeclosureSearch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        bbox,
        pageNumber,
        pageSize,
        preForeclosureSearchType,
        searchPhrase,
        latitude,
        longitude,
        radius,
      }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "marketSearch.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function recentSaleSearch({
  pageNumber,
  pageSize,
  latitude,
  longitude,
}: recentSaleSearchParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/recentSaleSearch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        pageNumber,
        pageSize,
        latitude,
        longitude,
      }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "nearbySaleSearch.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function propertyReportDifference({
  addressLine1,
  addressLine2,
  city,
  state,
  zip,
  versionA,
  versionB,
}: PropertyReportDifferenceParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/propertyReportDifference", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        addressLine1,
        addressLine2,
        city,
        state,
        zip,
        versionA,
        versionB,
      }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "propertyReportDifference.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data.success);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function getNeighborhoodPOIs({
  zip,
  zip4,
  variant,
}: GetPropertyPOIsParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/getNeighborhoodPOIs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ zip, zip4, variant }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "getNeighborhoodPOIs.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data.result);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function socialSearch({ search }: SocialSearch) {
  return new Promise((resolve, reject) => {
    fetch("/api/socialProfileSearch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ search }),
    })
      .then(response => {
        if (response.status === 401) {
          store.dispatch(
            logoutWithRedirect({
              location: "socialSearch.authenticate",
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store
            .dispatch(rateLimitAsync({ action: "socialSearch" }))
            .then(() => {
              store.dispatch(
                logoutWithRedirect({
                  location: "socialSearch.rateLimit",
                }),
              );
              reject({ message: "Rate Limit, authorization denied" });
            });
        } else {
          return response.json();
        }
      })
      .then(data => {
        const variant =
          store.getState().authentication?.memberExperience?.variant;
        if (data.success) {
          if (variant === "m48") {
            store.dispatch(setShowAccelerateBanner(true));
          }
          resolve(data);
        } else {
          if (data.message === "Plan Exceeded") {
            if (store.getState().subscription.isInTrial) {
              if (variant === "m48") {
                store.dispatch(setShowSecondaryAccelerate(true));
              } else {
                store.dispatch(setAccelerateBilling());
              }
            } else {
              alert(
                "Your membership has exceeded normal usage for the given billing period.  Please contact member support to resolve this issue.",
              );
            }
            console.log("Social:", data.message);
          }
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function pullPhoneReport(phoneNumber: PullPhoneReportParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/phoneSearch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ phoneNumber }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "pullPhoneReport.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store
            .dispatch(rateLimitAsync({ action: "pullPhoneReport" }))
            .then(() => {
              store.dispatch(
                logoutWithRedirect({
                  location: "pullPhoneReport.rateLimit",
                }),
              );
              reject({ message: "Rate Limit, authorization denied" });
            });
        } else {
          return response.json();
        }
      })
      .then(data => {
        const accountType = store.getState().authentication.accountType;
        const variant =
          store.getState().authentication?.memberExperience?.variant;
        if (data.success) {
          if (accountType !== "phone" && variant === "m48") {
            store.dispatch(setShowAccelerateBanner(true));
          }
          resolve(data);
        } else {
          if (data.message === "Plan Exceeded") {
            if (store.getState().subscription.isInTrial) {
              if (accountType !== "phone" && variant === "m48") {
                store.dispatch(setShowSecondaryAccelerate(true));
              } else {
                store.dispatch(setAccelerateBilling());
              }
            } else {
              alert(
                "Your membership has exceeded normal usage for the given billing period.  Please contact member support to resolve this issue.",
              );
            }
            console.log("Phone:", data.message);
          }
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function submitComplaint({
  isCall,
  MultipleFrequency,
  RecordedMessage,
  phoneNumber,
}: SubmitComplaintParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/submitPhoneComplaint", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        isCall,
        MultipleFrequency,
        RecordedMessage,
        phoneNumber,
      }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "submitComplaint.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store
            .dispatch(rateLimitAsync({ action: "submitPhoneComplaint" }))
            .then(() => {
              store.dispatch(
                logoutWithRedirect({
                  location: "submitComplaint.rateLimit",
                }),
              );
              reject({ message: "Rate Limit, authorization denied" });
            });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function getRentData({
  AddressLine1,
  State,
  City,
  Zip,
}: GetRentDataParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/getRentData", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        AddressLine1,
        State,
        City,
        Zip,
      }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "getRentData.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store.dispatch(rateLimitAsync({ action: "getRentData" })).then(() => {
            store.dispatch(
              logoutWithRedirect({
                location: "getRentData.rateLimit",
              }),
            );
            reject({ message: "Rate Limit, authorization denied" });
          });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function pullVehicleReport({
  vin,
  fields,
  state,
  plate,
  carType,
  miles,
}: PullVehicleReportParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/pullVehicleReport", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ vin, fields, state, plate, carType, miles }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "pullVehicleReport.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store
            .dispatch(rateLimitAsync({ action: "pullVehicleReport" }))
            .then(() => {
              store.dispatch(
                logoutWithRedirect({
                  location: "pullVehicleReport.rateLimit",
                }),
              );
              reject({ message: "Rate Limit, authorization denied" });
            });
        } else {
          return response.json();
        }
      })
      .then(data => {
        const accountType = store.getState().authentication.accountType;
        const variant =
          store.getState().authentication?.memberExperience?.variant;
        if (data.success) {
          if (accountType !== "vehicle" && variant === "m48") {
            store.dispatch(setShowAccelerateBanner(true));
          }
          resolve(data);
        } else {
          if (data.message === "Plan Exceeded") {
            if (store.getState().subscription.isInTrial) {
              if (accountType !== "vehicle" && variant === "m48") {
                store.dispatch(setShowSecondaryAccelerate(true));
              } else {
                store.dispatch(setAccelerateBilling());
              }
            } else {
              alert(
                "Your membership has exceeded normal usage for the given billing period.  Please contact member support to resolve this issue.",
              );
            }
            console.log("Auto:", data.message);
          }
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function pullVehicleMarketEstimate({
  vin,
  carType,
  miles,
}: PullVehicleMarketEstimateParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/pullVehicleMarketEstimate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ vin, carType, miles }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "pullVehicleMarketEstimate.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store
            .dispatch(rateLimitAsync({ action: "pullVehicleMarketEstimate" }))
            .then(() => {
              store.dispatch(
                logoutWithRedirect({
                  location: "pullVehicleMarketEstimate.rateLimit",
                }),
              );
              reject({ message: "Rate Limit, authorization denied" });
            });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data);
        } else {
          if (data.message === "Plan Exceeded") {
            //TODO: HANDLE USER NOTIFICATION (Should this happen in a component?)
            alert(
              "Your membership has exceeded normal usage for the given billing period.  Please contact member support to resolve this issue.",
            );
            console.log("Auto:", data.message);
          }
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function pullNeighborhoodReportByLocation({
  addressSearch,
  searchMethod,
}: PullNeighborhoodReportParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/searchByAddress", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ addressSearch, searchMethod }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "pullNeighborhoodReportByLocation.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store
            .dispatch(rateLimitAsync({ action: "neighborhoodSearchByAddress" }))
            .then(() => {
              store.dispatch(
                logoutWithRedirect({
                  location: "pullNeighborhoodReportByLocation.rateLimit",
                }),
              );
              reject({ message: "Rate Limit, authorization denied" });
            });
        } else {
          return response.json();
        }
      })
      .then(data => {
        const accountType = store.getState().authentication.accountType;
        const variant =
          store.getState().authentication?.memberExperience?.variant;
        if (data.success) {
          if (accountType !== "property" && variant === "m48") {
            store.dispatch(setShowAccelerateBanner(true));
          }
          resolve(data.report);
        } else {
          if (data.message === "Plan Exceeded") {
            if (store.getState().subscription.isInTrial) {
              if (accountType !== "property" && variant === "m48") {
                store.dispatch(setShowSecondaryAccelerate(true));
              } else {
                store.dispatch(setAccelerateBilling());
              }
            } else {
              alert(
                "Your membership has exceeded normal usage for the given billing period.  Please contact member support to resolve this issue.",
              );
            }
            console.log("Neighborhood:", data.message);
          }
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function pullNeighborhoodSearch({
  firstName,
  lastName,
}: PullNeighborhoodSearchParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/searchByName", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ firstName, lastName }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "pullNeighborhoodSearch.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store
            .dispatch(rateLimitAsync({ action: "neighborhoodSearchByName" }))
            .then(() => {
              store.dispatch(
                logoutWithRedirect({
                  location: "pullNeighborhoodSearch.rateLimit",
                }),
              );
              reject({ message: "Rate Limit, authorization denied" });
            });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data.search);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

/* List API Routes */
export function getAllLists() {
  return new Promise((resolve, reject) => {
    fetch("/api/getSavedLists", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "getAllLists.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store.dispatch(rateLimitAsync({ action: "getAllLists" })).then(() => {
            store.dispatch(
              logoutWithRedirect({
                location: "getAllLists.rateLimit",
              }),
            );
            reject({ message: "Rate Limit, authorization denied" });
          });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data.data);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function getSpecificList({ specificListName }: GetSpecificListParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/getSavedLists", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ specificListName }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "getSpecificList.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store
            .dispatch(rateLimitAsync({ action: "getSpecificList" }))
            .then(() => {
              store.dispatch(
                logoutWithRedirect({
                  location: "getSpecificList.rateLimit",
                }),
              );
              reject({ message: "Rate Limit, authorization denied" });
            });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data.data);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function addReportToList({
  listName,
  reportType,
  reportParams,
  reportDetails,
  displayName,
}: CombinedAddReportToList) {
  return new Promise((resolve, reject) => {
    fetch("/api/addSavedReport", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        listName,
        reportParams,
        reportType,
        reportDetails,
        displayName,
      }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "addReportToList.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store
            .dispatch(rateLimitAsync({ action: "addReportToList" }))
            .then(() => {
              store.dispatch(
                logoutWithRedirect({
                  location: "addReportToList.rateLimit",
                }),
              );
              reject({ message: "Rate Limit, authorization denied" });
            });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data.data);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function removeReportFromList({
  savedReportId,
  savedReportsListId,
}: RemoveReportFromListParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/removeSavedReport", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        savedReportId,
        savedReportsListId,
      }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "removeReportFromList.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store
            .dispatch(rateLimitAsync({ action: "removeReportFromList" }))
            .then(() => {
              store.dispatch(
                logoutWithRedirect({
                  location: "removeReportFromList.rateLimit",
                }),
              );
              reject({ message: "Rate Limit, authorization denied" });
            });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data.data);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function renameList({ listName, savedReportsListId }: RenameListParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/updateSavedReportsList", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        listName,
        savedReportsListId,
      }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "renameList.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store.dispatch(rateLimitAsync({ action: "renameList" })).then(() => {
            store.dispatch(
              logoutWithRedirect({
                location: "renameList.rateLimit",
              }),
            );
            reject({ message: "Rate Limit, authorization denied" });
          });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data.data);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function deleteList({ savedReportsListId }: DeleteListParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/removeSavedReportsList", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        savedReportsListId,
      }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "deleteList.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store.dispatch(rateLimitAsync({ action: "deleteList" })).then(() => {
            store.dispatch(
              logoutWithRedirect({
                location: "deleteList.rateLimit",
              }),
            );
            reject({ message: "Rate Limit, authorization denied" });
          });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data.data);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

/* Notes API Routes */
export function getAllReportsWithNotes() {
  return new Promise((resolve, reject) => {
    fetch("/api/getAllReportsWithNotes", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "getAllReportsWithNotes.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store
            .dispatch(rateLimitAsync({ action: "getAllReportsWithNotes" }))
            .then(() => {
              store.dispatch(
                logoutWithRedirect({
                  location: "getAllReportsWithNotes.rateLimit",
                }),
              );
              reject({ message: "Rate Limit, authorization denied" });
            });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data.data);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function getSpecificReportNotes({
  reportType,
  reportParams,
}: CombinedNoteParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/getSpecificReportNotes", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ reportType, reportParams }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "getSpecificReportNotes.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store
            .dispatch(rateLimitAsync({ action: "getSpecificReportNotes" }))
            .then(() => {
              store.dispatch(
                logoutWithRedirect({
                  location: "getSpecificReportNotes.rateLimit",
                }),
              );
              reject({ message: "Rate Limit, authorization denied" });
            });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data.data);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function addReportNote({
  note,
  reportType,
  reportParams,
  reportDetails,
  displayName,
}: CombinedNoteParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/addReportNote", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        reportParams,
        reportType,
        reportDetails,
        displayName,
        note,
      }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "addReportNote.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store
            .dispatch(rateLimitAsync({ action: "addReportNote" }))
            .then(() => {
              store.dispatch(
                logoutWithRedirect({
                  location: "addReportNote.rateLimit",
                }),
              );
              reject({ message: "Rate Limit, authorization denied" });
            });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data.data);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function removeReportNote({ reportNoteId }: RemoveReportNoteParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/removeReportNote", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        reportNoteId,
      }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "removeReportNote.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store
            .dispatch(rateLimitAsync({ action: "removeReportNote" }))
            .then(() => {
              store.dispatch(
                logoutWithRedirect({
                  location: "removeReportNote.rateLimit",
                }),
              );
              reject({ message: "Rate Limit, authorization denied" });
            });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data.data);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}

export function updateReportNote({
  reportNoteId,
  note,
}: UpdateReportNoteParams) {
  return new Promise((resolve, reject) => {
    fetch("/api/updateReportNote", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        reportNoteId,
        note,
      }),
    })
      .then(async response => {
        if (response.status === 401) {
          const body = await response.json();
          store.dispatch(
            logoutWithRedirect({
              location: "updateReportNote.authenticate",
              logError: body.message,
            }),
          );
          reject({ message: "No token, authorization denied" });
        } else if (response.status === 429) {
          store
            .dispatch(rateLimitAsync({ action: "updateReportNote" }))
            .then(() => {
              store.dispatch(
                logoutWithRedirect({
                  location: "updateReportNote.rateLimit",
                }),
              );
              reject({ message: "Rate Limit, authorization denied" });
            });
        } else {
          return response.json();
        }
      })
      .then(data => {
        if (data.success) {
          resolve(data.data);
        } else {
          reject(data);
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}
